/* eslint-env browser */
/**
 * alpha-inspector-browser.js
 * Pure browser JavaScript utilities to detect alpha-channel presence & usage,
 * extract an alpha mask, and apply a mask as alpha — no Node backend required.
 *
 * Notes:
 * - Uses Canvas + ImageBitmap when available.
 * - Uses only `window` (no `self`, no `globalThis`) to satisfy common ESLint rules.
 * - Avoids mixing ?? with || to satisfy parsers/linters.
 */

'use strict';

// -------------------------- Public API --------------------------

/**
 * Analyze a Blob/File: format, header transparency hints, and pixel-level usage.
 * @param {Blob} blob
 * @returns {Promise<{
 *   format: string,
 *   mime: string,
 *   supportsAlpha: boolean,
 *   header: any,
 *   pixel: null | {
 *     width:number,height:number,alphaUsed:boolean,minAlpha:number,maxAlpha:number,
 *     counts:{opaque:number,transparent:number,partial:number,total:number},
 *     ratios:{opaque:number,transparent:number,partial:number},
 *     bbox: null | {x:number,y:number,width:number,height:number},
 *     histogram:number[]
 *   },
 *   alphaUsed: boolean,
 *   summary: { hasAlphaChannelDeclared: boolean, alphaUsed: boolean, reason: string },
 *   warnings: string[]
 * }>}
 */
async function analyzeImage(blob) {
  const buf = await blob.arrayBuffer();
  const meta = detectFormatAndParseHeaders(buf);

  // Try pixel-level scan (may throw if canvas becomes tainted for cross-origin images).
  let pixel = null;
  let pixelError = null;
  try {
    pixel = await scanAlphaUsage(blob);
  } catch (e) {
    pixelError = String((e && e.message) || e);
  }

  const supportsAlpha = !!meta.supportsAlpha;

  // Header-level signal: "does the container claim to carry alpha?"
  let headerSaysHasAlphaChannel = false;
  if (meta.format === 'png' && meta.png) {
    headerSaysHasAlphaChannel = !!meta.png.hasAlphaChannel; // true RGBA/GA
  } else if (meta.format === 'webp' && meta.webp) {
    headerSaysHasAlphaChannel =
      !!(meta.webp.declaresAlpha || meta.webp.hasALPH || (meta.webp.vp8l && meta.webp.vp8l.alphaIsUsed === 1));
  } else if (meta.format === 'gif') {
    // GIF doesn't have a per-pixel alpha channel; it may have 1-bit transparency via a Transparent Color Flag.
    headerSaysHasAlphaChannel = false;
  } else if (meta.format === 'avif') {
    // AVIF supports alpha as an auxiliary plane; full header parse omitted here.
    headerSaysHasAlphaChannel = true;
  }

  // Header-based fallback for "alpha USED?" if pixel scan failed
  const headerFallback =
    (meta.format === 'png' && meta.png && meta.png.hasTRNS === true) || // PNG tRNS => transparency used
    (meta.format === 'webp' && meta.webp && meta.webp.vp8l && meta.webp.vp8l.alphaIsUsed === 1) ||
    (meta.format === 'gif' && meta.gif && meta.gif.hasTransparentColorFlag === true) ||
    false;

  // Prefer pixel truth when available; otherwise headers
  const alphaUsed = (pixel && typeof pixel.alphaUsed !== 'undefined') ? pixel.alphaUsed : headerFallback;

  return {
    format: meta.format,
    mime: meta.mime,
    supportsAlpha,
    header: meta,
    pixel,
    alphaUsed,
    summary: {
      hasAlphaChannelDeclared: !!headerSaysHasAlphaChannel,
      alphaUsed,
      reason: pixel ? 'computed from pixels' : 'inferred from headers'
    },
    warnings: pixelError ? [pixelError] : []
  };
}

/**
 * Extract the current alpha channel as an 8-bit gray PNG Blob.
 * If the source has no alpha, the output will be a solid white PNG.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
async function extractAlphaAsGrayPNG(blob) {
  const bitmap = await toImageBitmap(blob);
  const w = bitmap.width;
  const h = bitmap.height;

  const canvas = (typeof window !== 'undefined' && typeof window.OffscreenCanvas !== 'undefined')
    ? new window.OffscreenCanvas(w, h)
    : createCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: true });

  ctx.drawImage(bitmap, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  const d = id.data;

  // Write the alpha into RGB and force output alpha to 255 (opaque)
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    d[i + 0] = a;
    d[i + 1] = a;
    d[i + 2] = a;
    d[i + 3] = 255;
  }
  ctx.putImageData(id, 0, 0);

  if (bitmap && typeof bitmap.close === 'function') bitmap.close();
  return toBlobPNG(canvas);
}

/**
 * Apply a grayscale/luma mask as the alpha channel of an image.
 * Returns a PNG Blob (preserves alpha).
 * @param {Blob} baseBlob
 * @param {Blob} maskBlob
 * @param {{invert?:boolean, normalize?:boolean}} [options]
 * @returns {Promise<Blob>}
 */
async function applyMaskAsAlpha(baseBlob, maskBlob, options) {
  const opt = Object.assign({ invert: false, normalize: false }, options || {});
  const base = await toImageBitmap(baseBlob);
  const mask = await toImageBitmap(maskBlob);

  const w = base.width;
  const h = base.height;

  const maskCanvas = (typeof window !== 'undefined' && typeof window.OffscreenCanvas !== 'undefined')
    ? new window.OffscreenCanvas(w, h)
    : createCanvas(w, h);
  const mc = maskCanvas.getContext('2d', { willReadFrequently: true, alpha: false });
  mc.drawImage(mask, 0, 0, w, h);
  const mdata = mc.getImageData(0, 0, w, h).data;

  const outCanvas = (typeof window !== 'undefined' && typeof window.OffscreenCanvas !== 'undefined')
    ? new window.OffscreenCanvas(w, h)
    : createCanvas(w, h);
  const oc = outCanvas.getContext('2d', { willReadFrequently: true, alpha: true });
  oc.drawImage(base, 0, 0);

  const id = oc.getImageData(0, 0, w, h);
  const d = id.data;

  for (let i = 0, j = 0; i < d.length; i += 4, j += 4) {
    // Simple average luma; swap for Rec.709 if you like (0.2126R + 0.7152G + 0.0722B)
    let m = (mdata[j] + mdata[j + 1] + mdata[j + 2]) / 3;
    if (opt.normalize) m = (m < 128) ? 0 : 255;
    if (opt.invert) m = 255 - m;
    d[i + 3] = m;
  }

  oc.putImageData(id, 0, 0);

  if (base && typeof base.close === 'function') base.close();
  if (mask && typeof mask.close === 'function') mask.close();

  return toBlobPNG(outCanvas);
}

// -------------------- Format detection & header parsing --------------------

/**
 * @param {ArrayBuffer} buf
 * @returns {{
 *   format: string, mime: string, supportsAlpha: boolean,
 *   png?: { colorType:number|null, bitDepth:number|null, hasTRNS:boolean, hasAlphaChannel:boolean },
 *   webp?: { declaresAlpha:boolean, hasALPH:boolean, vp8l: null | { alphaIsUsed:0|1 } },
 *   gif?: { hasTransparentColorFlag:boolean },
 *   avif?: { headerParsed:boolean }
 * }}
 */
function detectFormatAndParseHeaders(buf) {
  const u8 = new Uint8Array(buf);
  const dv = new DataView(buf);

  function asASCII(o, n) {
    let out = '';
    for (let i = 0; i < n; i++) out += String.fromCharCode(u8[o + i]);
    return out;
  }

  // Signatures / magic
  const isPNG = u8.length >= 8 &&
    u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4E && u8[3] === 0x47 &&
    u8[4] === 0x0D && u8[5] === 0x0A && u8[6] === 0x1A && u8[7] === 0x0A;

  const isGIF = u8.length >= 6 && asASCII(0, 3) === 'GIF' && (asASCII(3, 3) === '89a' || asASCII(3, 3) === '87a');

  const isWEBP = u8.length >= 12 && asASCII(0, 4) === 'RIFF' && asASCII(8, 4) === 'WEBP';

  const isJPEG = u8.length >= 2 && u8[0] === 0xFF && u8[1] === 0xD8;

  const isAVIF = u8.length >= 12 && asASCII(4, 4) === 'ftyp' &&
    (asASCII(8, 4) === 'avif' || asASCII(8, 4) === 'avis');

  if (isPNG) {
    const png = parsePNG(dv);
    return { format: 'png', mime: 'image/png', supportsAlpha: true, png };
  }
  if (isWEBP) {
    const webp = parseWEBP(dv);
    return { format: 'webp', mime: 'image/webp', supportsAlpha: true, webp };
  }
  if (isGIF) {
    const gif = parseGIF(dv);
    return { format: 'gif', mime: 'image/gif', supportsAlpha: true, gif };
  }
  if (isAVIF) {
    // Full AVIF alpha detection requires ISO-BMFF parsing; we only mark capability here.
    return { format: 'avif', mime: 'image/avif', supportsAlpha: true, avif: { headerParsed: false } };
  }
  if (isJPEG) {
    return { format: 'jpeg', mime: 'image/jpeg', supportsAlpha: false };
  }
  return { format: 'unknown', mime: '', supportsAlpha: false };
}

function parsePNG(dv) {
  // After the 8-byte signature, PNG stores chunks: [length(4, BE)][type(4)][data][crc(4)]
  let o = 8;
  let colorType = null;
  let bitDepth = null;
  let hasTRNS = false;

  while (o + 8 <= dv.byteLength) {
    const len = dv.getUint32(o, false); o += 4;
    const t0 = dv.getUint8(o), t1 = dv.getUint8(o + 1), t2 = dv.getUint8(o + 2), t3 = dv.getUint8(o + 3);
    const type = String.fromCharCode(t0, t1, t2, t3); o += 4;

    if (type === 'IHDR') {
      // width(4) height(4) bitDepth(1) colorType(1) compression(1) filter(1) interlace(1)
      bitDepth = dv.getUint8(o + 8);
      colorType = dv.getUint8(o + 9);
    } else if (type === 'tRNS') {
      hasTRNS = true;
    }

    o += len + 4; // skip data + CRC
    if (type === 'IEND') break;
  }

  const hasAlphaChannel = (colorType === 4 || colorType === 6); // GA or RGBA
  return { colorType, bitDepth, hasTRNS, hasAlphaChannel };
}

function parseWEBP(dv) {
  // RIFF header at 0..11; then chunks: [fourcc(4)][size(4 LE)][payload][padding if odd].
  let o = 12;
  let declaresAlpha = false;
  let hasALPH = false;
  let vp8l = null;

  while (o + 8 <= dv.byteLength) {
    const c0 = dv.getUint8(o), c1 = dv.getUint8(o + 1), c2 = dv.getUint8(o + 2), c3 = dv.getUint8(o + 3);
    const fourcc = String.fromCharCode(c0, c1, c2, c3); o += 4;
    const size = dv.getUint32(o, true); o += 4;

    if (fourcc === 'VP8X' && size >= 10) {
      // VP8X feature flags: bit mask includes Alpha flag; we check it conservatively.
      const flags = dv.getUint8(o);
      const alphaFlag = (flags & 0x10) !== 0; // Alpha bit
      if (alphaFlag) declaresAlpha = true;
    } else if (fourcc === 'ALPH') {
      hasALPH = true;
    } else if (fourcc === 'VP8L' && size >= 5) {
      // Lossless header: 0x2f + 4 bytes -> [14w][14h][alpha_is_used(1)][version(3)]
      const sig = dv.getUint8(o);
      if (sig === 0x2f) {
        const header = dv.getUint32(o + 1, true);
        const alphaIsUsed = (header >>> 28) & 1;
        vp8l = { alphaIsUsed };
      }
    }

    o += size + (size & 1); // even-size padding
  }

  return { declaresAlpha: declaresAlpha || hasALPH, hasALPH, vp8l };
}

function parseGIF(dv) {
  // GIF header (6) + Logical Screen Descriptor (7)
  let o = 6 + 7;

  // Check Global Color Table flag in LSD (byte 10 from start)
  const packed = dv.getUint8(10);
  const gctFlag = (packed & 0x80) !== 0;
  if (gctFlag) {
    const gctSize = 3 * (1 << ((packed & 0x07) + 1));
    o += gctSize;
  }

  let hasTransparentColorFlag = false;

  while (o < dv.byteLength) {
    const b = dv.getUint8(o++);

    if (b === 0x21) {
      // Extension
      const label = dv.getUint8(o++);
      if (label === 0xF9) {
        // Graphics Control Extension
        const blockSize = dv.getUint8(o++); // should be 4
        const packedFields = dv.getUint8(o); // transparency flag is LSB
        hasTransparentColorFlag = (packedFields & 0x01) !== 0;
        o += blockSize; // skip rest of GCE payload
        o += 1; // block terminator
      } else {
        // Skip generic extension (sub-blocks)
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const sub = dv.getUint8(o++); if (sub === 0) break; o += sub;
        }
      }
    } else if (b === 0x2C) {
      // Image Descriptor
      o += 9; // left, top, w, h, packed
      const lctPacked = dv.getUint8(o - 1);
      const lctFlag = (lctPacked & 0x80) !== 0;
      if (lctFlag) {
        const lctSize = 3 * (1 << ((lctPacked & 0x07) + 1));
        o += lctSize;
      }
      // Skip image data (LZW): code size + sub-blocks
      o += 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const sub2 = dv.getUint8(o++); if (sub2 === 0) break; o += sub2;
      }
    } else if (b === 0x3B) {
      // Trailer
      break;
    } else {
      break;
    }
  }

  return { hasTransparentColorFlag };
}

// ------------------------ Pixel scan via Canvas ------------------------

/**
 * Decode and scan alpha usage from pixels (Canvas RGBA).
 * Returns histogram, bbox of non-opaque area, & usage flags.
 * @param {Blob} blob
 */
async function scanAlphaUsage(blob) {
  const bitmap = await toImageBitmap(blob);
  const w = bitmap.width;
  const h = bitmap.height;

  const canvas = (typeof window !== 'undefined' && typeof window.OffscreenCanvas !== 'undefined')
    ? new window.OffscreenCanvas(w, h)
    : createCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: true });

  // Both ImageBitmap and HTMLCanvasElement are valid CanvasImageSource:
  ctx.drawImage(bitmap, 0, 0);

  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  const hist = new Uint32Array(256);
  let min = 255, max = 0;
  let opaque = 0, transparent = 0, partial = 0;

  let xMin = w, yMin = h, xMax = -1, yMax = -1;
  let x = 0, y = 0;

  for (let i = 3; i < data.length; i += 4) {
    const a = data[i];
    hist[a]++;

    if (a < min) min = a;
    if (a > max) max = a;

    if (a === 255) {
      opaque++;
    } else {
      if (a === 0) transparent++; else partial++;
      // track bbox of non-opaque pixels
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    }

    x++;
    if (x === w) { x = 0; y++; }
  }

  const total = (data.length / 4) >>> 0;
  const alphaUsed = min < 255;

  const bbox = (xMax >= 0)
    ? { x: xMin, y: yMin, width: xMax - xMin + 1, height: yMax - yMin + 1 }
    : null;

  if (bitmap && typeof bitmap.close === 'function') bitmap.close();

  return {
    width: w, height: h,
    alphaUsed,
    minAlpha: min, maxAlpha: max,
    counts: { opaque, transparent, partial, total },
    ratios: {
      opaque: total ? (opaque / total) : 0,
      transparent: total ? (transparent / total) : 0,
      partial: total ? (partial / total) : 0
    },
    bbox,
    histogram: Array.from(hist)
  };
}

/**
 * Create an ImageBitmap when possible; falls back to a Canvas as a CanvasImageSource.
 * Uses only `window` to avoid ESLint issues with `self`/`globalThis`.
 * @param {Blob} blob
 * @returns {Promise<ImageBitmap|HTMLCanvasElement>}
 */
async function toImageBitmap(blob) {
  const hasCreateImageBitmap = (typeof window !== 'undefined' && typeof window.createImageBitmap === 'function');

  if (hasCreateImageBitmap) {
    // Keep color channels unmodified by alpha; alpha values are unaffected either way.
    return window.createImageBitmap(blob, { premultiplyAlpha: 'none' });
  }

  // Fallback via <img> -> draw to canvas -> return canvas
  const URLCtor = (typeof window !== 'undefined' && (window.URL || window.webkitURL));
  const url = URLCtor.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const c = createCanvas(img.naturalWidth, img.naturalHeight);
    const ctx = c.getContext('2d', { willReadFrequently: true, alpha: true });
    ctx.drawImage(img, 0, 0);
    if (hasCreateImageBitmap) return window.createImageBitmap(c);
    return c; // Canvas is a valid CanvasImageSource
  } finally {
    URLCtor.revokeObjectURL(url);
  }
}

// ---------------------------- Helpers ----------------------------

/** Create a standard canvas of given size. */
function createCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

/**
 * Load an image by URL (Object URL or remote).
 * Note: For remote URLs, you must serve CORS headers to avoid a tainted canvas.
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise(function (res, rej) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { res(img); };
    img.onerror = function (e) { rej(e); };
    img.src = url;
  });
}

/**
 * Get a PNG Blob from a Canvas or OffscreenCanvas.
 * @param {HTMLCanvasElement|OffscreenCanvas} canvas
 * @returns {Promise<Blob>}
 */
function toBlobPNG(canvas) {
  return new Promise(function (resolve) {
    // OffscreenCanvas only: convertToBlob
    if (typeof canvas.convertToBlob === 'function') {
      canvas.convertToBlob({ type: 'image/png' }).then(resolve);
      return;
    }
    // HTMLCanvasElement: toBlob
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(function (b) { resolve(b); }, 'image/png');
      return;
    }
    // Fallback: dataURL -> Blob
    const dataURL = canvas.toDataURL('image/png');
    const base64 = dataURL.split(',')[1];
    const bin = atob(base64);
    const u8 = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    resolve(new Blob([u8], { type: 'image/png' }));
  });
}

/** Receives results from analyzeImage. */
async function classifyAlpha(report) {
    const p = report && report.pixel;
    if (!p) return { maskPresent: false, reason: 'No pixel data' };
  
    const coverage = 1 - p.ratios.opaque;          // % of pixels with alpha < 255
    const softness = p.ratios.partial;              // % of semi-transparent pixels
    const binaryish = softness <= 0.01;             // ≤1% semi-transparent → mask is mostly hard edges
  
    // Consider it a "real" mask if at least 1% of pixels are non-opaque.
    const maskPresent =
      !!report.summary.hasAlphaChannelDeclared &&
      p.alphaUsed &&
      coverage >= 0.01;
  
    // Confidence heuristic: high if mask is sizable and mostly binary
    let confidence = 0;
    if (maskPresent) {
      confidence = (coverage >= 0.05 ? 0.7 : 0.5) + (binaryish ? 0.2 : 0.0);
      if (coverage >= 0.25) confidence += 0.1;
      if (confidence > 1) confidence = 1;
    }
  
    return {
      maskPresent,
      coverage,          // 0..1  (here ≈ 0.594)
      softness,          // 0..1  (here ≈ 0.0015)
      binaryish,         // true → basically a hard mask with slight antialiasing
      nonOpaqueBBox: p.bbox,
      confidence
    };
  }
  

// --------------------------- Module exports ---------------------------

export {
  analyzeImage,
  extractAlphaAsGrayPNG,
  applyMaskAsAlpha,
  detectFormatAndParseHeaders,
  classifyAlpha
};

// If you’re NOT using ES modules, comment out the export above and uncomment below:
// (function attachToWindow() {
//   window.AlphaInspector = { analyzeImage, extractAlphaAsGrayPNG, applyMaskAsAlpha, detectFormatAndParseHeaders };
// })();
